import { useEffect, useState } from 'react';
import {
  FieldType,
  FilterByField,
  SortOrder,
  useGetTerminalsQuery,
} from '../../../api/graphql/generated';
import {
  UsageSummary,
  mapStats,
} from '../../../pages/reports/usagesummary/components/usage-summary-column-definitions';

export function useTerminals({
  queryLimit,
  queryPage,
  queryFields,
  querySort,
  querySearch,
  queryFilterByFields,
  queryStartDate,
  queryEndDate,
}: GetTerminalsParams) {
  const [items, setItems] = useState<UsageSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const { data, loading } = useGetTerminalsQuery({
    variables: {
      limit: queryLimit,
      page: queryPage,
      filter: {
        sort: querySort.map((sort) => ({
          sort_by: sort.sort_by,
          sort_order: sort.sort_order,
        })),
        search_string: querySearch,
        fields: queryFields,
        filter_by_field: queryFilterByFields,
      },
      date_start: queryStartDate.toISOString().split('T')[0],
      date_end: queryEndDate.toISOString().split('T')[0],
    },
  });

  useEffect(() => {
    setIsLoading(loading);
    if (!loading && data?.getTerminals) {
      const { terminals, last_page, total } = data.getTerminals;
      setPageCount(last_page ?? 0);
      setTotalCount(total ?? 0);
      const mappedItems: UsageSummary[] =
        terminals?.map((terminal) => {
          // !! This is hacky and should be changed at the data collection layer
          // !! Basically as of now, when the usage is collected from the 3.7, the tx and rx are swapped
          // !! The API returns the usage from the perspective of the controller, so from the perspective of the terminal
          // !! the tx from the station is the rx for the terminal
          // !! This was midigated before by swapping the table column definitions, but that seems worse than this
          // !! The usage needs to be switched here for Curis and Skybits, as well as in the 'terminalUsageSplitPanel.tsx' file
          if (terminal?.network?.network_name?.toLowerCase().includes("skybits")){
            return {
              TerminalId: terminal?.terminal_id
                ? parseInt(terminal.terminal_id, 10)
                : 0,
              // Swap the tx and rx that is shown due to the backend error
              TxBytes: terminal?.usage_summary?.rx_bytes ?? "N/A",
              RxBytes: terminal?.usage_summary?.tx_bytes ?? "N/A",
              TotalBytes: terminal?.usage_summary?.total_bytes ?? "N/A",
              Status: terminal?.terminal_status ?? "N/A",
              CompanyName: terminal?.company?.company_name ?? "N/A",
              TerminalName: terminal?.terminal_name ?? "N/A",
              TerminalAltName: terminal?.terminal_alt_name ?? "N/A",
              ServicePlan: terminal?.terminal_service_plan ?? "N/A",
              ServicePlanUsageBased:
                terminal?.terminal_service_plan_usage_based ?? "N/A",
              NetworkName: terminal?.network?.network_name ?? "N/A",
            };
          } else {
            return {
              TerminalId: terminal?.terminal_id
                ? parseInt(terminal.terminal_id, 10)
                : 0,
              TxBytes: terminal?.usage_summary?.tx_bytes ?? 'N/A',
              RxBytes: terminal?.usage_summary?.rx_bytes ?? 'N/A',
              TotalBytes: terminal?.usage_summary?.total_bytes ?? 'N/A',
              Status: terminal?.terminal_status ?? 'N/A',
              CompanyName: terminal?.company?.company_name ?? 'N/A',
              TerminalName: terminal?.terminal_name ?? 'N/A',
              TerminalAltName: terminal?.terminal_alt_name ?? 'N/A',
              ServicePlan: terminal?.terminal_service_plan ?? 'N/A',
              ServicePlanUsageBased: terminal?.terminal_service_plan_usage_based ?? 'N/A',
              NetworkName: terminal?.network?.network_name ?? 'N/A',
            };
          }
        }) ?? [];
      setItems(mappedItems);
    }
  }, [loading, data]);

  return {
    items,
    isLoading,
    pageCount,
    totalCount,
  };
}

export function useTerminalsForMap({ queryLimit }: GetTerminalsForMapParams) {
  const [items, setItems] = useState<mapStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const { data, loading } = useGetTerminalsQuery({
    variables: {
      limit: queryLimit,
      page: 1,
    },
  });

  useEffect(() => {
    setIsLoading(loading);
    if (!loading && data?.getTerminals?.terminals) {
      const mappedItems: mapStats[] =
        data.getTerminals.terminals.map((terminal) => {
          const lat = terminal?.terminal_latitude;
          const lon = terminal?.terminal_longitude;
          const isValidLat =
            lat !== null && lat !== undefined && lat >= -90 && lat <= 90;
          const isValidLon =
            lon !== null && lon !== undefined && lon >= -180 && lon <= 180;

          return {
            TerminalId: terminal?.terminal_id
              ? parseInt(terminal.terminal_id, 10)
              : 0,
            TerminalName: terminal?.terminal_name ?? 'N/A',
            terminalLat: isValidLat ? lat : null,
            terminalLon: isValidLon ? lon : null,
          };
        }) ?? [];
      setItems(mappedItems);
    }
  }, [loading, data]);

  return {
    items,
    isLoading,
  };
}

interface GetTerminalsParams {
  queryLimit: number;
  queryPage: number;
  queryFields: FieldType[];
  querySort: SortOrder[];
  querySearch: string;
  queryFilterByFields: FilterByField[];
  queryStartDate: Date;
  queryEndDate: Date;
}

interface GetTerminalsForMapParams {
  queryLimit: number;
}
